import { useMemo, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  CalendarBlank,
  BookOpen,
  X,
  Gavel,
  Users,
  CheckCircle,
  CaretDown,
  CaretRight,
  WarningCircle,
  CalendarX,
  MagnifyingGlass as Search,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RowActions } from '@/components/shared/RowActions';
import { useHearingsStore, Hearing } from '@/lib/hearingsStore';
import { searchHearings } from '@/lib/hearingsApi';
import { useServerSearch } from '@/lib/useServerSearch';
import { TablePagination, pageSlice, PAGE_SIZE_OPTIONS } from '@/components/shared/TablePagination';
import {
  LEGAL_OUTCOME_TYPES,
  OUTCOME_NOTES_MAX,
  OUTCOME_SUMMARY_MIN,
  outcomeSummaryError,
  outcomeTypeErrorFor,
} from '@/lib/hearingConstants';
import { isMeaningfulTitle } from '@/lib/validators';
import { useAuthStore } from '@/features/auth/store';
import { canRecordHearingOutcome } from '@/lib/pupilUiBoundary';
import { useChambersMode } from '@/lib/useChambersMode';
import { HearingBriefPanel } from '@/components/hearings/HearingBriefPanel';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { toast } from 'sonner';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * The same six windows the admin/clerk hearings list offers (HearingsPage.tsx:38), so a
 * barrister narrows their own court list on the same terms a clerk uses on the phone to them.
 * None is selected by default: the page keeps its Today / This week / Upcoming / Past sections
 * until a window is picked, and only then collapses into one flat list.
 */
const TIME_FILTERS = [
  { label: 'Upcoming',   match: 'upcoming' },
  { label: 'Today',      match: 'today' },
  { label: 'This week',  match: 'this_week' },
  { label: 'Next week',  match: 'next_week' },
  { label: 'This month', match: 'this_month' },
  { label: 'Past',       match: 'past' },
];

function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

function daysUntil(isoDate: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate + 'T00:00:00'); target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000);
}

// ─── Status text — plain, no pills ─────────────────────────────────────────────

function StatusText({ status, type }: { status: string; type?: string }) {
  const labels: Record<string, { text: string; cls: string }> = {
    adjourned:           { text: 'Adjourned',           cls: 'text-amber-600 dark:text-amber-400' },
    awaiting_appearance: { text: 'Awaiting appearance', cls: 'text-amber-600 dark:text-amber-400' },
    awaiting_compliance: { text: 'Awaiting compliance', cls: 'text-amber-600 dark:text-amber-400' },
    order_reserved:      { text: 'Order reserved',      cls: 'text-violet-600 dark:text-violet-400' },
    judgment_pending:    { text: 'Judgment pending',    cls: 'text-violet-600 dark:text-violet-400' },
    completed:           { text: 'Completed',           cls: 'text-muted-foreground/50' },
    vacated:             { text: 'Vacated',             cls: 'text-red-500' },
    no_show:             { text: 'No show',             cls: 'text-red-500' },
    in_progress:         { text: 'In progress',         cls: 'text-brand-600 dark:text-brand-400' },
    tentative:           { text: 'Tentative',           cls: 'text-muted-foreground/50' },
  };
  // scheduled / confirmed are default — not shown
  const statusInfo = labels[status];
  if (!type && !statusInfo) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {type && <span className="text-muted-foreground/60">{type}</span>}
      {type && statusInfo && <span className="text-muted-foreground/30">·</span>}
      {statusInfo && <span className={statusInfo.cls}>{statusInfo.text}</span>}
    </div>
  );
}

// ─── Next action helper ─────────────────────────────────────────────────────────

function getNextAction(h: Hearing): { label: string; urgent: boolean } | null {
  if (['completed', 'vacated', 'disposed'].includes(h.status)) return null;
  const days = daysUntil(h.date);
  if (h.status === 'adjourned' && !h.nextHearingDate) return { label: 'Schedule next hearing', urgent: true };
  if (h.status === 'order_reserved' || h.status === 'judgment_pending') return { label: 'Await judgment', urgent: false };
  if (h.status === 'awaiting_compliance') return { label: 'Chase compliance', urgent: true };
  if (days < 0 && !h.outcome) return { label: 'Record outcome', urgent: true };
  if (days === 0) return { label: 'In court today', urgent: true };
  if (days <= 2) return { label: 'Prepare hearing brief', urgent: true };
  return null;
}

// ─── Hearing card ───────────────────────────────────────────────────────────────

function HearingCard({
  h,
  todayKey,
  briefHearingId,
  setBriefHearingId,
  onRecordOutcome,
  showOutcomePending,
  isSolo,
}: {
  h: Hearing;
  todayKey: string;
  briefHearingId: string | null;
  setBriefHearingId: (id: string | null) => void;
  /** Omit to draw no outcome control — a pupil may amend the listing, never the result. */
  onRecordOutcome?: (h: Hearing) => void;
  showOutcomePending?: boolean;
  isSolo: boolean;
}) {
  const navigate = useNavigate();
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const days = daysUntil(h.date);
  const isToday = h.date === todayKey;

  const terminalStatuses = ['completed', 'vacated', 'disposed', 'adjourned'];
  const isUrgent = days >= 0 && days <= 2 && !terminalStatuses.includes(h.status);
  const hasOutcome = !!h.outcome;
  const isActive = !terminalStatuses.includes(h.status);
  const isBriefOpen = briefHearingId === h.id;
  const nextAction = getNextAction(h);
  const outcomePending =
    showOutcomePending &&
    !h.outcome &&
    h.date < todayKey &&
    h.status !== 'vacated' &&
    h.status !== 'adjourned';

  return (
    <div
      className={`rounded-xl border bg-card shadow-sm transition ${
        isUrgent
          ? 'border-amber-200/80 dark:border-amber-500/30'
          : isBriefOpen
          ? 'border-brand-300/60'
          : 'border-border/60 hover:border-border/80'
      }`}
    >
      <div className="p-4">
        {/* Top row: date · time · today-marker | type · status · ⋮ */}
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{fmtDate(h.date)}</span>
            <span className="text-muted-foreground/30">·</span>
            <span className="text-sm text-muted-foreground">{h.time}</span>
            {isToday && (
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Today</span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <StatusText status={h.status} type={h.type} />
            <RowActions
              onView={() => navigate(`/barrister/hearings/${h.id}`)}
              onEdit={() => navigate(`/barrister/hearings/${h.id}`)}
              onDelete={() => {
                void updateHearing(h.id, { status: 'vacated' })
                  .then((saved) => { if (saved) toast.success(`${h.matterName} — hearing cancelled`); });
              }}
              viewLabel="View"
              editLabel="Edit"
              deleteLabel="Cancel hearing"
              // A bin reads as "erase this record". This action vacates the listing and keeps it
              // on the matter for good, so it carries the non-destructive icon the component takes
              // for exactly this case (RowActions.tsx:44).
              deleteIcon={<CalendarX className="h-4 w-4" />}
              disableDelete={!isActive}
              deleteConfirm={{
                title: 'Cancel hearing?',
                message: `Cancel the hearing for “${h.matterName}”? This clears reminders and updates the diary.`,
                confirmLabel: 'Cancel hearing',
              }}
            />
          </div>
        </div>

        {/* Case name + number */}
        <div className="mb-3">
          <button
            className="text-left text-sm font-medium leading-snug text-foreground transition hover:text-brand-600 dark:hover:text-brand-400"
            onClick={() => { if (h.matterId) navigate(`/barrister/matters/${h.matterId}`); }}
          >
            {h.matterName}
          </button>
          {h.matterNumber && (
            <div className="mt-0.5 text-[10px] tracking-wide text-muted-foreground/60">
              {h.matterNumber}
            </div>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground/70">
            <MapPin className="h-3 w-3 shrink-0" />
            <span>{h.court}{h.courtroom ? ` · Room ${h.courtroom}` : ''}</span>
            {h.judge && (
              <>
                <span className="text-muted-foreground/30">·</span>
                <Gavel className="h-3 w-3 shrink-0" />
                <span>{h.judge}</span>
              </>
            )}
            {h.isVirtual && <span className="text-brand-600 dark:text-brand-400">Virtual</span>}
          </div>
        </div>

        {/* Team row — only show barrister names in chambers mode */}
        {(!isSolo && (h.leadBarrister || h.durationLabel)) && (
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground/70">
            {h.leadBarrister && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {h.leadBarrister}
                {h.juniorBarrister && (
                  <span className="text-muted-foreground/50"> + {h.juniorBarrister}</span>
                )}
              </span>
            )}
            {h.durationLabel && <span>{h.durationLabel}</span>}
          </div>
        )}
        {/* Solo mode: just duration */}
        {isSolo && h.durationLabel && (
          <div className="mb-3 text-xs text-muted-foreground/60">{h.durationLabel}</div>
        )}

        {/* Urgent next action — plain text, no pill */}
        {nextAction?.urgent && (
          <div className="mb-3 text-xs font-medium text-amber-600 dark:text-amber-400">{nextAction.label}</div>
        )}

        {/* Outcome pending */}
        {outcomePending && (
          <div className="mb-3 border-l-2 border-amber-300 dark:border-amber-500/40 py-1 pl-3 text-xs text-amber-700 dark:text-amber-300">
            Outcome not yet recorded
          </div>
        )}

        {/* Outcome recorded */}
        {hasOutcome && (
          <div className="mb-3 border-l-2 border-border py-1 pl-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">Outcome:</span> {h.outcome}
          </div>
        )}

        {/* Action row — only for hearings that need interaction */}
        {(isActive || isToday) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border/40 pt-3">
            <Button
              variant={isBriefOpen ? 'primary' : 'outline'}
              size="xs"
              leftIcon={isBriefOpen ? <X className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
              onClick={() => setBriefHearingId(isBriefOpen ? null : h.id)}
            >
              {isBriefOpen ? 'Close brief' : 'Prepare'}
            </Button>
            {isToday && isActive && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => navigate('/barrister/court-day')}
                className="border-brand-300 text-brand-700 dark:text-brand-300 hover:bg-brand-50"
              >
                Court Day
              </Button>
            )}
            {isActive && !hasOutcome && onRecordOutcome && (
              <Button
                variant="outline"
                size="xs"
                leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                onClick={() => onRecordOutcome(h)}
              >
                Record outcome
              </Button>
            )}
            {/* ADJOURNING IS RECORDING AN OUTCOME. The adjourn form writes `status: 'adjourned'`
                plus `outcome` / `nextHearingDate`, and `adjourned` is one of the result-bearing
                statuses `assertPupilMayNotSetOutcome` refuses (hearings.service.ts). It therefore
                belongs behind the SAME gate as "Record outcome" above — it was left open, so a
                pupil could still reach the form through `?adjourn=1` and 403 on submit. */}
            {isActive && onRecordOutcome && (
              <Button
                variant="ghost"
                size="xs"
                leftIcon={<WarningCircle className="h-3.5 w-3.5" />}
                // The hearing's own adjourn form (reason + next date), not a status-only update.
                onClick={() => navigate(`/barrister/hearings/${h.id}?adjourn=1`)}
              >
                Adjourn
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Inline brief panel */}
      {isBriefOpen && (
        <div className="border-t border-border/60 bg-muted/20 p-4">
          <div className="mx-auto max-w-2xl">
            <HearingBriefPanel
              hearingId={h.id}
              // Same signal the card was given: the parent withholds it from a pupil.
              onRecordOutcome={onRecordOutcome ? () => navigate(`/barrister/hearings/${h.id}`) : undefined}
              onOpenMatter={(matterId) => navigate(`/barrister/matters/${matterId}`)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section divider ────────────────────────────────────────────────────────────

function SectionDivider({
  label,
  count,
  highlight,
}: {
  label: string;
  count: number;
  highlight?: boolean;
}) {
  return (
    <div className="mb-3 mt-7 flex items-center gap-3 first:mt-0">
      <span
        className={`text-[10px] font-semibold uppercase tracking-widest ${
          highlight && count > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/50'
        }`}
      >
        {label}
      </span>
      <span className="text-[10px] text-muted-foreground/40">
        {count}
      </span>
      <div className="flex-1 border-t border-border/40" />
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────────

export default function MyHearingsPage() {
  const navigate = useNavigate();
  const allHearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const hearingsLoading = useHearingsStore((s) => s.loading);
  const hearingsError = useHearingsStore((s) => s.error);
  const wsConnected = useWebSocketStore((s) => s.connected);

  const [briefHearingId, setBriefHearingId] = useState<string | null>(null);
  const [pastExpanded, setPastExpanded] = useState(true);
  const [outcomeModal, setOutcomeModal] = useState<{ id: string; name: string } | null>(null);
  const [outcomeText, setOutcomeText] = useState('');
  const [outcomeType, setOutcomeType] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);
  // Shown only after a save attempt, so the modal doesn't open shouting at the user.
  const [outcomeSubmitted, setOutcomeSubmitted] = useState(false);
  const outcomeTypeError = outcomeTypeErrorFor(outcomeType, outcomeSubmitted);
  const outcomeTextError = outcomeSummaryError(outcomeText, isMeaningfulTitle, outcomeSubmitted);

  useEffect(() => { fetchHearings(); }, [fetchHearings]);
  useEffect(() => {
    const unsub = onCalendarUpdate(() => { fetchHearings(); });
    return unsub;
  }, [wsConnected, fetchHearings]);

  const user = useAuthStore((s) => s.user);
  const chambersMode = useChambersMode();
  const isSolo = chambersMode.isSolo && ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');

  // "Mine" means led or juniored by the signed-in barrister. Applied to the store list AND to
  // server search results below — a search must never widen this page beyond the user's own
  // court list, even though the server would return every hearing they are allowed to read.
  const isMine = useCallback(
    (h: Hearing) =>
      !!user &&
      !h.id.startsWith('temp-') &&
      (h.leadBarristerUserId === user.id || h.juniorBarristerUserId === user.id),
    [user],
  );

  const hearings = useMemo(() => {
    if (!user) return [];
    return allHearings
      .filter(isMine)
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  }, [allHearings, user, isMine]);

  // ── Search + window filter ───────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [timeFilter, setTimeFilter] = useState('');

  // Server-side search reaches hearings older than the client-loaded window (canonical hook H3,
  // as on the admin list). Results are re-filtered to this barrister's own hearings.
  const { isSearching, results: serverResults } = useServerSearch<Hearing>(search, {
    key: ['my-hearings-search'],
    fetcher: (q) => searchHearings(q),
  });

  const todayKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const weekEnd7 = useMemo(() => {
    const d = new Date(Date.now() + 7 * 86_400_000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const activeStatuses = ['scheduled', 'confirmed', 'in_progress', 'tentative', 'adjourned'];

  const todayHearings = useMemo(
    () => hearings.filter((h) => h.date === todayKey && activeStatuses.includes(h.status)),
    [hearings, todayKey],
  );
  const thisWeekHearings = useMemo(
    () => hearings.filter((h) => h.date > todayKey && h.date <= weekEnd7 && activeStatuses.includes(h.status)),
    [hearings, todayKey, weekEnd7],
  );
  const upcomingHearings = useMemo(
    () => hearings.filter((h) => h.date > weekEnd7 && activeStatuses.includes(h.status)),
    [hearings, weekEnd7],
  );
  const pastHearings = useMemo(
    () =>
      hearings
        .filter(
          (h) =>
            h.status === 'completed' ||
            h.status === 'vacated' ||
            (h.date < todayKey && !activeStatuses.includes(h.status)),
        )
        .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time)),
    [hearings, todayKey],
  );

  /**
   * The flat list. Shown INSTEAD of the sections while a window is selected or a search is
   * running — the sections are themselves a time split, so showing both would say the same
   * thing twice. Upcoming and Past keep this page's status rules (a cancelled hearing is past,
   * whatever its date); the four date windows answer "what was listed then", so they include
   * cancelled listings, badged as such.
   */
  const flatHearings = useMemo(() => {
    const source = isSearching ? serverResults.filter(isMine) : hearings;
    if (!timeFilter) return source;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + mondayOffset);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const nextWeekStart = new Date(weekStart);
    nextWeekStart.setDate(weekStart.getDate() + 7);
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return source.filter((h) => {
      const d = new Date(h.date + 'T00:00:00');
      const isLive = activeStatuses.includes(h.status);
      if (timeFilter === 'upcoming')   return d.getTime() >= today.getTime() && isLive;
      if (timeFilter === 'past')       return !isLive || d.getTime() < today.getTime();
      if (timeFilter === 'today')      return d.getTime() === today.getTime();
      if (timeFilter === 'this_week')  return d >= weekStart && d <= weekEnd;
      if (timeFilter === 'next_week')  return d >= nextWeekStart && d <= nextWeekEnd;
      if (timeFilter === 'this_month') return d >= monthStart && d <= monthEnd;
      return true;
    });
  }, [hearings, serverResults, isSearching, isMine, timeFilter]);

  /** Sections give way to the flat list the moment the barrister narrows the page. */
  const showFlatList = !!timeFilter || isSearching || !!search.trim();

  // ── Paging ───────────────────────────────────────────────────────────────────
  // Two lists here can grow without bound: the filtered/search list, and Past, which only ever
  // gets longer. Today / This week / Upcoming are bounded by their own windows and a barrister
  // wants to see all of those at once, so they are deliberately not paged.
  // One page size governs both lists; each keeps its own page number.
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]!);
  const [flatPage, setFlatPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  useEffect(() => { setFlatPage(1); }, [timeFilter, search]);
  const pagedFlat = pageSlice(flatHearings, flatPage, pageSize);
  const pagedPast = pageSlice(pastHearings, pastPage, pageSize);

  const upcomingCount = todayHearings.length + thisWeekHearings.length + upcomingHearings.length;
  const outcomesRecorded = hearings.filter((h) => h.outcome).length;

  const subtitleParts: string[] = [];
  if (upcomingCount > 0) subtitleParts.push(`${upcomingCount} upcoming`);
  if (outcomesRecorded > 0) subtitleParts.push(`${outcomesRecorded} outcome${outcomesRecorded !== 1 ? 's' : ''} recorded`);
  if (subtitleParts.length === 0) subtitleParts.push('Your court list');

  const handleRecordOutcome = (h: Hearing) => {
    setOutcomeModal({ id: h.id, name: h.matterName });
    setOutcomeText('');
    setOutcomeType('');
    setOutcomeSubmitted(false);
  };

  const cardProps = {
    todayKey,
    briefHearingId,
    setBriefHearingId,
    // assertPupilMayNotSetOutcome refuses a pupil server-side; don't draw the control.
    onRecordOutcome: canRecordHearingOutcome(user?.memberType) ? handleRecordOutcome : undefined,
    isSolo,
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Hearings"
        subtitle={subtitleParts.join(' · ')}
        actions={
          <>
            <Button variant="outline" size="md" onClick={() => navigate('/barrister/hearings/new?mode=record')}>
              Record past hearing
            </Button>
            <Button variant="primary" size="md" onClick={() => navigate('/barrister/hearings/new')}>
              Schedule hearing
            </Button>
          </>
        }
      />

      {hearingsError && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <span className="font-semibold">Could not load hearings.</span>
          <button onClick={() => fetchHearings()} className="font-semibold underline">Try again</button>
        </div>
      )}
      {hearingsLoading && allHearings.length === 0 && !hearingsError && (
        <div className="mb-4 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          Loading hearings…
        </div>
      )}

      {/* Search + window filters — the same six the clerk's list offers */}
      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Find one of your hearings — matter, court, judge…"
              className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-0.5 text-xs sm:w-full xl:w-auto">
            {TIME_FILTERS.map((f) => (
              <button
                key={f.match}
                // Clicking the selected window clears it, which puts the sections back.
                onClick={() => setTimeFilter((cur) => (cur === f.match ? '' : f.match))}
                className={`whitespace-nowrap rounded-sm px-3 py-1.5 font-medium transition sm:flex-1 sm:px-2 sm:text-center xl:flex-none xl:px-3 ${
                  timeFilter === f.match
                    ? 'bg-brand-600 text-white'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Flat, filtered list — replaces the sections while a window or a search is active */}
      {showFlatList && (
        <>
          <SectionDivider
            label={
              isSearching || search.trim()
                ? 'Search results'
                : TIME_FILTERS.find((f) => f.match === timeFilter)?.label ?? 'Filtered'
            }
            count={flatHearings.length}
          />
          {flatHearings.length === 0 ? (
            <Card className="flex flex-col items-center justify-center py-14 text-center">
              <CalendarBlank className="mb-3 h-9 w-9 text-muted-foreground/30" weight="duotone" />
              <p className="text-sm font-semibold text-foreground">No hearings match</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {search.trim()
                  ? 'Nothing on your court list matches that search.'
                  : 'Nothing of yours is listed in that window.'}
              </p>
              <Button
                className="mt-4"
                variant="outline"
                size="sm"
                onClick={() => { setSearch(''); setTimeFilter(''); }}
              >
                Clear filters
              </Button>
            </Card>
          ) : (
            <>
              <div className="space-y-3">
                {pagedFlat.map((h) => (
                  <HearingCard key={h.id} h={h} {...cardProps} showOutcomePending={h.date < todayKey} />
                ))}
              </div>
              <Card className="mt-3">
                <TablePagination
                  page={flatPage}
                  pageSize={pageSize}
                  total={flatHearings.length}
                  onPageChange={setFlatPage}
                  onPageSizeChange={setPageSize}
                  className="border-t-0"
                />
              </Card>
            </>
          )}
        </>
      )}

      {/* Today */}
      {!showFlatList && todayHearings.length > 0 && (
        <>
          <SectionDivider label="Today" count={todayHearings.length} highlight />
          <div className="space-y-3">
            {todayHearings.map((h) => (
              <HearingCard key={h.id} h={h} {...cardProps} />
            ))}
          </div>
        </>
      )}

      {/* This week */}
      {!showFlatList && thisWeekHearings.length > 0 && (
        <>
          <SectionDivider label="This week" count={thisWeekHearings.length} />
          <div className="space-y-3">
            {thisWeekHearings.map((h) => (
              <HearingCard key={h.id} h={h} {...cardProps} />
            ))}
          </div>
        </>
      )}

      {/* Upcoming */}
      {!showFlatList && upcomingHearings.length > 0 && (
        <>
          <SectionDivider label="Upcoming" count={upcomingHearings.length} />
          <div className="space-y-3">
            {upcomingHearings.map((h) => (
              <HearingCard key={h.id} h={h} {...cardProps} />
            ))}
          </div>
        </>
      )}

      {/* Past — expanded by default; the barrister can still collapse it. */}
      {!showFlatList && pastHearings.length > 0 && (
        <>
          <div className="mb-3 mt-7 flex items-center gap-3">
            <button
              className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 transition hover:text-muted-foreground"
              onClick={() => setPastExpanded((v) => !v)}
            >
              {pastExpanded ? (
                <CaretDown className="h-3 w-3" />
              ) : (
                <CaretRight className="h-3 w-3" />
              )}
              Past
            </button>
            <span className="text-[10px] text-muted-foreground/40">{pastHearings.length}</span>
            <div className="flex-1 border-t border-border/40" />
          </div>

          {pastExpanded && (
            <>
              <div className="space-y-3">
                {pagedPast.map((h) => (
                  <HearingCard key={h.id} h={h} {...cardProps} showOutcomePending />
                ))}
              </div>
              <Card className="mt-3">
                <TablePagination
                  page={pastPage}
                  pageSize={pageSize}
                  total={pastHearings.length}
                  onPageChange={setPastPage}
                  onPageSizeChange={setPageSize}
                  className="border-t-0"
                />
              </Card>
            </>
          )}
        </>
      )}

      {/* Empty state — only when the court list itself is empty, not when a filter found nothing */}
      {!showFlatList && hearings.length === 0 && !hearingsLoading && (
        <Card className="flex flex-col items-center justify-center py-16 text-center">
          <CalendarBlank className="mb-3 h-10 w-10 text-muted-foreground/30" weight="duotone" />
          <p className="text-sm font-semibold text-foreground">No hearings on your court list</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Hearings assigned to you will appear here.
          </p>
          <Button className="mt-4" variant="primary" size="sm" onClick={() => navigate('/barrister/hearings/new')}>
            Schedule hearing
          </Button>
        </Card>
      )}

      {/* Record outcome modal */}
      {outcomeModal && canRecordHearingOutcome(user?.memberType) && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-xl border border-border bg-card shadow-2xl">
            <div className="border-b border-border px-5 py-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                Record outcome
              </div>
              <div className="mt-1 text-base font-semibold text-foreground">{outcomeModal.name}</div>
            </div>
            <div className="px-5 py-4">
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Outcome type <span className="text-red-500">*</span>
              </label>
              <select
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 ${outcomeTypeError ? 'border-red-400 focus:ring-red-500/40' : 'border-border focus:ring-brand-500'}`}
                value={outcomeType}
                onChange={(e) => setOutcomeType(e.target.value)}
              >
                <option value="">Select outcome…</option>
                {LEGAL_OUTCOME_TYPES.filter((o) => o.value !== 'adjourned' && o.value !== 'trial_continued').map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className={`mb-3 mt-1 text-xs ${outcomeTypeError ? 'text-red-600 dark:text-red-400' : 'text-transparent'}`}>
                {outcomeTypeError || '.'}
              </p>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Outcome notes <span className="text-red-500">*</span>
              </label>
              <textarea
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${outcomeTextError ? 'border-red-400 focus:ring-red-500/40' : 'border-border focus:ring-brand-500'}`}
                rows={4}
                maxLength={OUTCOME_NOTES_MAX}
                placeholder="e.g. Judgment reserved. Next directions hearing listed for 14 August."
                value={outcomeText}
                onChange={(e) => setOutcomeText(e.target.value)}
                autoFocus
              />
              {outcomeTextError
                ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{outcomeTextError}</p>
                : <p className="mt-1 text-xs text-muted-foreground">At least {OUTCOME_SUMMARY_MIN} characters — this is what the matter record will show.</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
              <Button variant="outline" size="sm" onClick={() => setOutcomeModal(null)} disabled={savingOutcome}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={savingOutcome}
                onClick={async () => {
                  setOutcomeSubmitted(true);
                  if (outcomeTypeError || outcomeTextError || !outcomeType || !outcomeText.trim()) {
                    toast.error('Please complete the highlighted fields.');
                    return;
                  }
                  setSavingOutcome(true);
                  try {
                    // Keep the modal (and the typed outcome) open if the server refuses it.
                    if (!(await updateHearing(outcomeModal.id, { outcome: outcomeText.trim(), outcomeType, status: 'completed' }))) return;
                    toast.success('Outcome recorded');
                    setOutcomeModal(null);
                  } finally {
                    setSavingOutcome(false);
                  }
                }}
              >
                {savingOutcome ? 'Saving…' : 'Save outcome'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
